/**
 * Device-to-clinic association (localStorage).
 * First login stores account_id + clinic info + device_id for lock screen PIN lookup and rate limiting.
 * On localStorage errors we log and rethrow so callers see distinct error state (fail-closed).
 */

const KEY = "happydoc_device_association";
const DEVICE_ID_KEY = "happydoc_device_id";

export class DeviceAssociationStorageError extends Error {
    constructor(
        message: string,
        public readonly operation: "get" | "set" | "getDeviceId" | "setDeviceId",
        public readonly cause?: unknown,
    ) {
        super(message);
        this.name = "DeviceAssociationStorageError";
    }
}

export interface DeviceAssociation {
    account_id: string;
    clinic_name: string;
    clinic_location: string | null;
    device_id: string;
    established_at: number;
}

function randomUUID(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function getDeviceAssociation(): DeviceAssociation | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as DeviceAssociation;
        if (!parsed?.account_id || !parsed?.device_id) return null;
        return parsed;
    } catch (e) {
        console.error("[DeviceAssociation] getDeviceAssociation failed", e);
        throw new DeviceAssociationStorageError("Failed to read device association", "get", e);
    }
}

/**
 * Safe getter for use in render: returns null when localStorage is inaccessible
 * instead of throwing, so the app does not crash (e.g. in LockScreenOverlay).
 * Use getDeviceAssociation() when errors should be handled by the caller (effects, handlers).
 */
export function getDeviceAssociationSafe(): DeviceAssociation | null {
    try {
        return getDeviceAssociation();
    } catch (e) {
        if (e instanceof DeviceAssociationStorageError) return null;
        throw e;
    }
}

function getPersistedDeviceId(): string | null {
    try {
        const raw = localStorage.getItem(DEVICE_ID_KEY);
        return raw && /^[0-9a-f-]{36}$/i.test(raw) ? raw : null;
    } catch (e) {
        console.error("[DeviceAssociation] getPersistedDeviceId failed", e);
        throw new DeviceAssociationStorageError("Failed to read device id", "getDeviceId", e);
    }
}

function setPersistedDeviceId(device_id: string): void {
    try {
        localStorage.setItem(DEVICE_ID_KEY, device_id);
    } catch (e) {
        console.error("[DeviceAssociation] setPersistedDeviceId failed", e);
        throw new DeviceAssociationStorageError("Failed to persist device id", "setDeviceId", e);
    }
}

export function setDeviceAssociation(params: {
    account_id: string;
    clinic_name: string;
    clinic_location?: string | null;
}): DeviceAssociation {
    try {
        const existing = getDeviceAssociation();
        const persistedId = getPersistedDeviceId();
        const device_id = existing?.device_id ?? persistedId ?? randomUUID();
        if (!existing?.device_id && !persistedId) setPersistedDeviceId(device_id);
        const value: DeviceAssociation = {
            account_id: params.account_id,
            clinic_name: params.clinic_name,
            clinic_location: params.clinic_location ?? null,
            device_id,
            established_at: Date.now(),
        };
        localStorage.setItem(KEY, JSON.stringify(value));
        return value;
    } catch (e) {
        if (e instanceof DeviceAssociationStorageError) throw e;
        console.error("[DeviceAssociation] setDeviceAssociation failed", e);
        throw new DeviceAssociationStorageError("Failed to store device association", "set", e);
    }
}

export function getOrCreateDeviceId(): string {
    const existing = getDeviceAssociation();
    if (existing?.device_id) return existing.device_id;
    const persisted = getPersistedDeviceId();
    if (persisted) return persisted;
    const device_id = randomUUID();
    setPersistedDeviceId(device_id);
    return device_id;
}
